import { useState } from "react";
import { Hourglass, Plus, Pencil, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import SearchBox from "@/components/SearchBox";
import EmptyState from "@/components/EmptyState";
import api from "@/lib/api";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";
import { maskPhone } from "@/lib/masks";

const empty = {
  nome: "", data_nasc: "", telefone: "", email: "",
  responsavel_nome: "", diagnostico: "", prioridade: "media",
  origem: "", observacoes: "",
};

const PRIO_MAP = {
  alta: { label: "Alta", color: "bg-rose-100 text-rose-700 border-rose-200" },
  media: { label: "Média", color: "bg-amber-100 text-amber-700 border-amber-200" },
  baixa: { label: "Baixa", color: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function ListaEspera() {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("aguardando");
  const params = { q };
  if (statusFilter !== "todos") params.status = statusFilter;
  const { items, loading, setItems, refetch } = useCrudList("/lista-espera", params);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [convertId, setConvertId] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...empty, ...l }); setOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) {
        const upd = await crudUpdate("/lista-espera", editing.id, form);
        setItems((prev) => prev.map((x) => x.id === upd.id ? upd : x));
        toast.success("Item atualizado");
      } else {
        const c = await crudCreate("/lista-espera", form);
        setItems((prev) => [c, ...prev]);
        toast.success("Adicionado à lista de espera");
      }
      setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/lista-espera", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Removido");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setDeleteId(null); }
  };

  const handleConvert = async () => {
    try {
      await api.post(`/lista-espera/${convertId}/converter`);
      toast.success("Convertido em paciente");
      refetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setConvertId(null); }
  };

  return (
    <div data-testid="lista-espera-page">
      <PageHeader
        icon={Hourglass}
        title="Lista de Espera"
        description="Fila de espera com priorização e conversão em paciente ativo."
        actions={<Button onClick={openNew} data-testid="lista-new-btn"><Plus className="h-4 w-4 mr-1" />Nova inscrição</Button>}
      />

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por nome, responsável, diagnóstico…" testId="lista-search" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="aguardando">Aguardando</SelectItem>
            <SelectItem value="convertido">Convertidos</SelectItem>
            <SelectItem value="desistiu">Desistiram</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="p-6"><EmptyState icon={Hourglass} title="Fila vazia" description="Ninguém aguardando no momento." /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead><TableHead>Responsável</TableHead>
                <TableHead>Diagnóstico</TableHead><TableHead>Telefone</TableHead>
                <TableHead>Prioridade</TableHead><TableHead>Status</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} data-testid={`lista-row-${l.id}`}>
                    <TableCell className="font-medium">{l.nome}</TableCell>
                    <TableCell className="text-muted-foreground">{l.responsavel_nome || "—"}</TableCell>
                    <TableCell>{l.diagnostico || "—"}</TableCell>
                    <TableCell>{l.telefone || "—"}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${PRIO_MAP[l.prioridade]?.color || ""}`}>{PRIO_MAP[l.prioridade]?.label || l.prioridade}</span></TableCell>
                    <TableCell><Badge variant={l.status === "aguardando" ? "secondary" : "outline"}>{l.status}</Badge></TableCell>
                    <TableCell><div className="flex justify-end gap-1">
                      {l.status === "aguardando" && (
                        <Button size="icon" variant="ghost" onClick={() => setConvertId(l.id)} title="Converter em paciente" data-testid={`lista-convert-${l.id}`}><UserCheck className="h-4 w-4 text-primary" /></Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(l.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Editar inscrição" : "Nova inscrição"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Nome" req><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="lista-form-nome" /></F>
              <F label="Data nasc."><Input type="date" value={form.data_nasc || ""} onChange={(e) => setForm({ ...form, data_nasc: e.target.value })} /></F>
              <F label="Responsável"><Input value={form.responsavel_nome || ""} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} /></F>
              <F label="Telefone"><Input placeholder="(00) 00000-0000" value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} /></F>
              <F label="Email"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
              <F label="Diagnóstico"><Input value={form.diagnostico || ""} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} /></F>
              <F label="Prioridade">
                <Select value={form.prioridade} onValueChange={(v) => setForm({ ...form, prioridade: v })}>
                  <SelectTrigger data-testid="lista-form-prio"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="alta">Alta</SelectItem><SelectItem value="media">Média</SelectItem><SelectItem value="baixa">Baixa</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Origem"><Input value={form.origem || ""} placeholder="Portal, telefone, indicação…" onChange={(e) => setForm({ ...form, origem: e.target.value })} /></F>
            </div>
            <F label="Observações"><Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="lista-form-submit" className="bg-primary hover:bg-[#3B704E]">{saving ? "Salvando…" : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remover da lista?</AlertDialogTitle><AlertDialogDescription>Ação irreversível.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Remover</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!convertId} onOpenChange={(o) => !o && setConvertId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Converter em paciente?</AlertDialogTitle><AlertDialogDescription>Um novo paciente será criado com os dados desta inscrição. O item ficará marcado como convertido.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleConvert} className="bg-primary hover:bg-[#3B704E]" data-testid="lista-convert-confirm">Converter</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, req, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}
